/**
 * src/features/referral/ui/WhatsAppRow.tsx
 *
 * One-tap copy of the localized WhatsApp referral message (with {{url}} interpolated).
 * Uses the same 2s copy-confirmation pattern as InviteUrlRow.
 * T-27-04: Message rendered as React text content (auto-escaped).
 */

import { FC, useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageCircle, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useReducedMotion } from '@/shared/lib/hooks/useReducedMotion';

/** Duration in ms for which the copy confirmation state is shown. */
const COPY_CONFIRM_MS = 2000;

interface WhatsAppRowProps {
  url: string | null;
}

export const WhatsAppRow: FC<WhatsAppRowProps> = ({ url }) => {
  const { t } = useTranslation();
  const reduced = useReducedMotion();
  const [copied, setCopied] = useState(false);
  // Ref holds the pending reset timer so we can cancel it on re-click or unmount.
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear any pending timer on unmount to prevent setState after unmount.
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const handleCopy = useCallback(async () => {
    if (!url) return;
    const message = t(
      'referral.whatsapp_message',
      'Hey! I use Estimatix to build precise construction estimates in minutes. Try it free for a month with my invite link: {{url}}',
      { url },
    );
    let success = false;
    try {
      await navigator.clipboard.writeText(message);
      success = true;
    } catch {
      // Best-effort: clipboard unavailable; leave success=false
    }
    if (!success) return;
    // Cancel any previous pending reset before starting a new one.
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }
    setCopied(true);
    timerRef.current = setTimeout(() => {
      setCopied(false);
      timerRef.current = null;
    }, COPY_CONFIRM_MS);
  }, [url, t]);

  return (
    <button
      onClick={handleCopy}
      disabled={!url}
      className="w-full min-h-[44px] flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 hover:bg-green-50 dark:hover:bg-green-900/20 hover:border-green-200 dark:hover:border-green-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed group"
      aria-pressed={copied}
    >
      <MessageCircle
        size={18}
        className="text-green-600 dark:text-green-400 shrink-0 group-hover:text-green-700 dark:group-hover:text-green-300 transition-colors"
      />
      <span className="flex-1 text-left text-body text-slate-700 dark:text-slate-300 font-medium">
        {t('referral.whatsapp_cta', 'Send via WhatsApp')}
      </span>
      <AnimatePresence mode="wait">
        {copied ? (
          <motion.span
            key="whatsapp-copied"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: reduced ? 0 : 0.15 }}
            className="text-body text-green-600 dark:text-green-400 flex items-center gap-1"
          >
            <Check size={14} />
            {t('referral.whatsapp_copied', 'Message copied!')}
          </motion.span>
        ) : null}
      </AnimatePresence>
    </button>
  );
};
